import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PricingCard from './components/PricingCard';
import FeatureComparisonTable from './components/FeatureComparisonTable';
import BillingManagement from './components/BillingManagement';

const PricingSubscriptionPlans = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currentPlan, setCurrentPlan] = useState('basic');

  const pricingPlans = [
    {
      id: 'basic',
      name: 'Basic',
      description: 'Perfect for small businesses getting started',
      monthlyPrice: 49,
      annualPrice: 470,
      recommended: false,
      features: [
        { name: 'Job Postings', value: '5 per month', included: true },
        { name: 'Candidate Visibility', value: 'Standard', included: true },
        { name: 'Analytics Dashboard', value: 'Basic', included: true },
        { name: 'Application Management', value: 'Unlimited', included: true },
        { name: 'Email Support', value: '48h response', included: true },
        { name: 'Candidate Matching', value: 'Basic algorithm', included: true },
        { name: 'Priority Listing', value: 'Not included', included: false },
        { name: 'Advanced Analytics', value: 'Not included', included: false },
        { name: 'API Access', value: 'Not included', included: false },
        { name: 'Dedicated Support', value: 'Not included', included: false }
      ],
      limits: {
        jobPostings: 5,
        activeJobs: 3,
        candidateViews: 100
      }
    },
    {
      id: 'professional',
      name: 'Professional',
      description: 'Advanced features for growing companies',
      monthlyPrice: 149,
      annualPrice: 1430,
      recommended: true,
      features: [
        { name: 'Job Postings', value: 'Unlimited', included: true },
        { name: 'Candidate Visibility', value: 'Enhanced', included: true },
        { name: 'Analytics Dashboard', value: 'Advanced with custom reports', included: true },
        { name: 'Application Management', value: 'Unlimited', included: true },
        { name: 'Email Support', value: '24h response', included: true },
        { name: 'Candidate Matching', value: 'Enhanced AI algorithm', included: true },
        { name: 'Priority Listing', value: 'Top 3 placement', included: true },
        { name: 'Advanced Analytics', value: 'Full access', included: true },
        { name: 'Bulk Actions', value: 'Included', included: true },
        { name: 'API Access', value: 'Not included', included: false },
        { name: 'Dedicated Support', value: 'Not included', included: false }
      ],
      limits: {
        jobPostings: 'unlimited',
        activeJobs: 'unlimited',
        candidateViews: 'unlimited'
      }
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'Complete solution for large organizations',
      monthlyPrice: 399,
      annualPrice: 3830,
      recommended: false,
      features: [
        { name: 'Job Postings', value: 'Unlimited', included: true },
        { name: 'Candidate Visibility', value: 'Premium', included: true },
        { name: 'Analytics Dashboard', value: 'Enterprise with exports', included: true },
        { name: 'Application Management', value: 'Unlimited', included: true },
        { name: 'Priority Support', value: '4h response', included: true },
        { name: 'Candidate Matching', value: 'Premium AI with custom training', included: true },
        { name: 'Priority Listing', value: 'Top placement guaranteed', included: true },
        { name: 'Advanced Analytics', value: 'Full access + predictive insights', included: true },
        { name: 'Bulk Actions', value: 'Advanced automation', included: true },
        { name: 'API Access', value: 'Full REST API', included: true },
        { name: 'Dedicated Account Manager', value: 'Included', included: true },
        { name: 'White-label Branding', value: 'Custom branding options', included: true },
        { name: 'SLA Guarantee', value: '99.9% uptime', included: true }
      ],
      limits: {
        jobPostings: 'unlimited',
        activeJobs: 'unlimited',
        candidateViews: 'unlimited'
      }
    }
  ];

  const handlePlanSelect = (planId) => {
    console.log('Selected plan:', planId);
    // Handle plan selection logic
  };

  const handleContactSales = () => {
    console.log('Contact sales clicked');
    // Handle contact sales logic
  };

  const getPrice = (plan) => {
    return billingCycle === 'monthly' ? plan?.monthlyPrice : plan?.annualPrice;
  };

  const getSavingsPercentage = () => {
    return 20; // 20% savings on annual billing
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        {/* Header Section */}
        <div className="bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Icon name="Sparkles" size={16} />
                <span>Flexible Pricing Plans</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
                Choose the Perfect Plan for Your Hiring Needs
              </h1>
              <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                Transparent pricing with powerful features to help you find and hire the best talent
              </p>

              {/* Billing Toggle */}
              <div className="flex items-center justify-center gap-4 bg-card rounded-xl p-2 max-w-md mx-auto border border-border shadow-warm-sm">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all ${
                    billingCycle === 'monthly' ?'bg-primary text-primary-foreground shadow-md' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all relative ${
                    billingCycle === 'annual' ?'bg-primary text-primary-foreground shadow-md' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Annual
                  <span className="absolute -top-2 -right-2 bg-success text-success-foreground text-xs px-2 py-0.5 rounded-full">
                    Save {getSavingsPercentage()}%
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Cards Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
            {pricingPlans?.map((plan) => (
              <PricingCard
                key={plan?.id}
                plan={plan}
                billingCycle={billingCycle}
                price={getPrice(plan)}
                isCurrentPlan={currentPlan === plan?.id}
                onSelect={() => handlePlanSelect(plan?.id)}
                onContactSales={handleContactSales}
              />
            ))}
          </div>

          {/* Feature Comparison Table */}
          <div className="mb-12">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-3">
                Compare All Features
              </h2>
              <p className="text-muted-foreground">
                Detailed comparison of features across all plans
              </p>
            </div>
            <FeatureComparisonTable plans={pricingPlans} />
          </div>

          {/* Billing Management Section */}
          <div className="mb-12">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-3">
                Manage Your Subscription
              </h2>
              <p className="text-muted-foreground">
                View usage, upgrade plans, and manage billing
              </p>
            </div>
            <BillingManagement currentPlan={currentPlan} pricingPlans={pricingPlans} />
          </div>

          {/* FAQ Section */}
          <div className="bg-card rounded-xl p-6 md:p-8 border border-border shadow-warm-sm">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-6 text-center">
              Frequently Asked Questions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Icon name="HelpCircle" size={18} className="text-accent" />
                  Can I change plans anytime?
                </h3>
                <p className="text-sm text-muted-foreground">
                  Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately with prorated billing.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Icon name="HelpCircle" size={18} className="text-accent" />
                  What payment methods do you accept?
                </h3>
                <p className="text-sm text-muted-foreground">
                  We accept all major credit cards, bank transfers, and invoice billing for Enterprise plans.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Icon name="HelpCircle" size={18} className="text-accent" />
                  Is there a free trial?
                </h3>
                <p className="text-sm text-muted-foreground">
                  Yes, all plans come with a 14-day free trial. No credit card required to start.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Icon name="HelpCircle" size={18} className="text-accent" />
                  What happens if I exceed my limits?
                </h3>
                <p className="text-sm text-muted-foreground">
                  We'll notify you when approaching limits. You can upgrade anytime or purchase add-ons for additional capacity.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-12 bg-gradient-to-br from-primary to-primary/80 rounded-xl p-8 md:p-12 text-center">
            <Icon name="Headphones" size={48} className="mx-auto mb-4 text-primary-foreground" />
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-primary-foreground mb-3">
              Need Help Choosing?
            </h2>
            <p className="text-primary-foreground/90 mb-6 max-w-2xl mx-auto">
              Our team is here to help you find the perfect plan for your organization's needs
            </p>
            <Button
              variant="secondary"
              size="lg"
              iconName="MessageCircle"
              onClick={handleContactSales}
            >
              Contact Sales Team
            </Button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default PricingSubscriptionPlans;